import React, { useState, useEffect } from 'react';
import axios from '../config/axiosConfig'; 
import './AdminDashboard.css';
import { 
  FaUsers, FaMoneyBillWave, FaClipboardList, FaChartBar, 
  FaCheckCircle, FaTimesCircle, FaUserShield, FaCog,
  FaDatabase, FaBell, FaUserPlus, FaChartLine, FaCalendar,
  FaFileExport, FaFilter, FaSearch, FaEye, FaEdit, FaTrash,
  FaLock, FaUnlock, FaUserCheck, FaUserTimes, FaDownload
} from 'react-icons/fa';

const AdminDashboard = ({ user }) => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchDashboardData();
  }, [dateRange]);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, usersRes, requestsRes, donationsRes] = await Promise.all([
        axios.get('https://hsn.urugowoc.org.rw/backend/api/admin/stats', { params: dateRange }),
        axios.get('https://hsn.urugowoc.org.rw/backend/api/admin/users'),
        axios.get('https://hsn.urugowoc.org.rw/backend/api/admin/requests', { params: dateRange }),
        axios.get('https://hsn.urugowoc.org.rw/backend/api/admin/donations', { params: dateRange })
      ]);
      
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setRequests(requestsRes.data);
      setDonations(donationsRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateUserStatus = async (userId, status) => {
    try {
      await axios.put(`https://hsn.urugowoc.org.rw/backend/api/admin/users/${userId}/status`, { status });
      fetchDashboardData();
    } catch (error) {
      console.error('Error updating user status:', error);
    }
  };

  const deleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await axios.delete(`https://hsn.urugowoc.org.rw/backend/api/admin/users/${userId}`);
        fetchDashboardData();
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  };

  const exportData = async (type) => {
    try {
      const response = await axios.get(`https://hsn.urugowoc.org.rw/backend/api/admin/export/${type}`, {
        responseType: 'blob',
        params: dateRange
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting data:', error);
    }
  };

  const filteredUsers = users.filter(user => 
    user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Loading Admin Dashboard...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="container">
        {/* Dashboard Header */}
        <div className="admin-header">
          <div className="header-content">
            <h1><FaUserShield /> Admin Dashboard</h1>
            <p>Welcome back, {user?.full_name}. Manage the entire Urugo WOC platform</p>
          </div>
          <div className="header-actions">
            <button className="btn btn-primary" onClick={() => exportData('all')}>
              <FaFileExport /> Export All Data
            </button>
          </div>
        </div>

        {/* Filter Controls */}
        <div className="filter-controls">
          <div className="date-filter">
            <FaCalendar />
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
            />
            <span>to</span>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
            />
          </div>
          <div className="search-filter">
            <FaSearch />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Stats Overview */}
        <div className="stats-overview">
          <div className="stat-card primary">
            <div className="stat-icon">
              <FaUsers />
            </div>
            <div className="stat-content">
              <h3>{stats?.total_users || 0}</h3>
              <p>Total Users</p>
              <span className="stat-trend">+12% from last month</span>
            </div>
          </div>

          <div className="stat-card success">
            <div className="stat-icon">
              <FaClipboardList />
            </div>
            <div className="stat-content">
              <h3>{stats?.total_requests || 0}</h3>
              <p>Total Requests</p>
              <span className="stat-trend">+8% from last month</span>
            </div>
          </div>

          <div className="stat-card warning">
            <div className="stat-icon">
              <FaMoneyBillWave />
            </div>
            <div className="stat-content">
              <h3>{(stats?.total_revenue || 0).toLocaleString()} RWF</h3>
              <p>Total Revenue</p>
              <span className="stat-trend">+15% from last month</span>
            </div>
          </div>

          <div className="stat-card info">
            <div className="stat-icon">
              <FaChartBar />
            </div>
            <div className="stat-content">
              <h3>{stats?.active_women || 0}</h3>
              <p>Active Women</p>
              <span className="stat-trend">+5% from last month</span>
            </div>
          </div>
        </div>

        {/* Dashboard Tabs */}
        <div className="dashboard-tabs">
          <button 
            className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <FaChartLine /> Overview
          </button>
          <button 
            className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            <FaUsers /> Users
          </button>
          <button 
            className={`tab-btn ${activeTab === 'requests' ? 'active' : ''}`}
            onClick={() => setActiveTab('requests')}
          >
            <FaClipboardList /> Requests
          </button>
          <button 
            className={`tab-btn ${activeTab === 'donations' ? 'active' : ''}`}
            onClick={() => setActiveTab('donations')}
          >
            <FaMoneyBillWave /> Donations
          </button>
          <button 
            className={`tab-btn ${activeTab === 'reports' ? 'active' : ''}`}
            onClick={() => setActiveTab('reports')}
          >
            <FaDatabase /> Reports
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="tab-content">
            <div className="overview-grid">
              {/* Recent Activity */}
              <div className="overview-card">
                <div className="card-header">
                  <h3><FaBell /> Recent Activity</h3>
                  <button className="btn-link" onClick={() => exportData('activity')}>
                    <FaDownload /> Export
                  </button>
                </div>
                <div className="activity-list">
                  {stats?.recent_activity?.map((activity, index) => (
                    <div key={index} className="activity-item">
                      <div className="activity-icon">
                        {activity.type === 'user' && <FaUserPlus />}
                        {activity.type === 'request' && <FaClipboardList />}
                        {activity.type === 'payment' && <FaMoneyBillWave />}
                      </div>
                      <div className="activity-details">
                        <p>{activity.message}</p>
                        <span className="activity-time">{activity.time}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* User Statistics */}
              <div className="overview-card">
                <div className="card-header">
                  <h3><FaUsers /> User Statistics</h3>
                </div>
                <div className="user-stats">
                  <div className="stat-item">
                    <span className="stat-label">Total Users</span>
                    <span className="stat-value">{stats?.total_users || 0}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Employers</span>
                    <span className="stat-value">{stats?.employer_count || 0}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Active Today</span>
                    <span className="stat-value">{stats?.active_today || 0}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">New This Month</span>
                    <span className="stat-value">{stats?.new_this_month || 0}</span>
                  </div>
                </div>
                <div className="chart-placeholder">
                  <FaChartLine className="chart-icon" />
                  <p>User growth chart will appear here</p>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="overview-card">
                <div className="card-header">
                  <h3><FaCog /> Quick Actions</h3>
                </div>
                <div className="quick-actions-grid">
                  <button className="action-btn" onClick={() => setActiveTab('users')}>
                    <FaUserPlus /> Add New User
                  </button>
                  <button className="action-btn" onClick={() => exportData('all')}>
                    <FaFileExport /> Export All Data
                  </button>
                  <button className="action-btn" onClick={() => window.location.href = '/manager-dashboard'}>
                    <FaChartBar /> View Manager Dashboard
                  </button>
                  <button className="action-btn">
                    <FaDatabase /> Backup Database
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="tab-content">
            <div className="table-header">
              <h2>User Management</h2>
              <div className="table-actions">
                <button className="btn btn-primary" onClick={() => window.location.href = '/register'}>
                  <FaUserPlus /> Add User
                </button>
                <button className="btn btn-outline" onClick={() => exportData('users')}>
                  <FaFileExport /> Export Users
                </button>
              </div>
            </div>

            <div className="table-responsive">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Username</th>
                    <th>Full Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>User Type</th>
                    <th>Status</th>
                    <th>Joined</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(user => (
                    <tr key={user.id}>
                      <td>{user.id}</td>
                      <td>
                        <div className="user-cell">
                          <div className="user-avatar-small">
                            {user.username.charAt(0).toUpperCase()}
                          </div>
                          {user.username}
                        </div>
                      </td>
                      <td>{user.full_name || 'N/A'}</td>
                      <td>{user.email}</td>
                      <td>{user.phone || 'N/A'}</td>
                      <td>
                        <span className={`user-type-badge type-${user.user_type}`}>
                          {user.user_type}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge status-${user.status || 'active'}`}>
                          {user.status || 'active'}
                        </span>
                      </td>
                      <td>{new Date(user.created_at).toLocaleDateString()}</td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="btn-icon view"
                            onClick={() => setSelectedUser(user)}
                            title="View Details"
                          >
                            <FaEye />
                          </button>
                          <button 
                            className="btn-icon edit"
                            onClick={() => window.location.href = `/admin/users/${user.id}/edit`}
                            title="Edit User"
                          >
                            <FaEdit />
                          </button>
                          {user.status === 'active' ? (
                            <button 
                              className="btn-icon disable"
                              onClick={() => updateUserStatus(user.id, 'suspended')}
                              title="Suspend User"
                            >
                              <FaLock />
                            </button>
                          ) : (
                            <button 
                              className="btn-icon enable"
                              onClick={() => updateUserStatus(user.id, 'active')}
                              title="Activate User"
                            >
                              <FaUnlock />
                            </button>
                          )}
                          <button 
                            className="btn-icon delete"
                            onClick={() => deleteUser(user.id)}
                            title="Delete User"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredUsers.length === 0 && (
              <div className="empty-state">
                <FaUsers size={48} />
                <h3>No Users Found</h3>
                <p>No users match your search criteria.</p>
              </div>
            )}
          </div>
        )}

        {/* Requests Tab */}
        {activeTab === 'requests' && (
          <div className="tab-content">
            <div className="table-header">
              <h2>Request Management</h2>
              <div className="table-actions">
                <button className="btn btn-outline" onClick={() => exportData('requests')}>
                  <FaFileExport /> Export Requests
                </button>
              </div>
            </div>

            <div className="table-responsive">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Employer</th>
                    <th>Department</th>
                    <th>Employees</th>
                    <th>Duration</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map(request => (
                    <tr key={request.id}>
                      <td>REQ-{request.id}</td>
                      <td>
                        <div className="user-cell">
                          <div className="user-avatar-small">
                            {request.employer_name?.charAt(0) || 'E'}
                          </div>
                          <div>
                            <strong>{request.employer_name}</strong>
                            <small>{request.employer_email}</small>
                          </div>
                        </div>
                      </td>
                      <td>{request.department_name}</td>
                      <td>{request.number_of_employees}</td>
                      <td>{request.duration_days} days</td>
                      <td>{request.total_amount.toLocaleString()} RWF</td>
                      <td>
                        <span className={`status-badge status-${request.status}`}>
                          {request.status}
                        </span>
                      </td>
                      <td>{new Date(request.created_at).toLocaleDateString()}</td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="btn-icon view"
                            onClick={() => window.location.href = `/request/${request.id}`}
                            title="View Request"
                          >
                            <FaEye />
                          </button>
                          {request.status === 'pending' && (
                            <>
                              <button 
                                className="btn-icon approve"
                                onClick={() => updateRequestStatus(request.id, 'approved')}
                                title="Approve Request"
                              >
                                <FaCheckCircle />
                              </button>
                              <button 
                                className="btn-icon reject"
                                onClick={() => updateRequestStatus(request.id, 'rejected')}
                                title="Reject Request"
                              >
                                <FaTimesCircle />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Donations Tab */}
        {activeTab === 'donations' && (
          <div className="tab-content">
            <div className="table-header">
              <h2>Donation Management</h2>
              <div className="table-actions">
                <button className="btn btn-outline" onClick={() => exportData('donations')}>
                  <FaFileExport /> Export Donations
                </button>
              </div>
            </div>

            <div className="table-responsive">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Donor</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Amount</th>
                    <th>Message</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {donations.map(donation => (
                    <tr key={donation.id}>
                      <td>DON-{donation.id}</td>
                      <td>{donation.donor_name}</td>
                      <td>{donation.donor_email || 'N/A'}</td>
                      <td>{donation.donor_phone || 'N/A'}</td>
                      <td>{donation.amount.toLocaleString()} RWF</td>
                      <td className="message-cell">
                        {donation.message ? (
                          <div className="truncate-text" title={donation.message}>
                            {donation.message.length > 50 ? donation.message.substring(0, 50) + '...' : donation.message}
                          </div>
                        ) : 'No message'}
                      </td>
                      <td>{new Date(donation.donation_date).toLocaleDateString()}</td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="btn-icon view"
                            onClick={() => window.location.href = `/donation/${donation.id}`}
                            title="View Details"
                          >
                            <FaEye />
                          </button>
                          <button 
                            className="btn-icon edit"
                            onClick={() => window.location.href = `/admin/donations/${donation.id}/edit`}
                            title="Edit Donation"
                          >
                            <FaEdit />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <div className="tab-content">
            <div className="reports-grid">
              <div className="report-card">
                <h3><FaFileExport /> Generate Reports</h3>
                <div className="report-options">
                  <button className="report-btn" onClick={() => exportData('users')}>
                    <FaUsers /> Users Report
                  </button>
                  <button className="report-btn" onClick={() => exportData('requests')}>
                    <FaClipboardList /> Requests Report
                  </button>
                  <button className="report-btn" onClick={() => exportData('donations')}>
                    <FaMoneyBillWave /> Donations Report
                  </button>
                  <button className="report-btn" onClick={() => exportData('all')}>
                    <FaDatabase /> Full Database Export
                  </button>
                </div>
              </div>

              <div className="report-card">
                <h3><FaChartBar /> System Statistics</h3>
                <div className="system-stats">
                  <div className="system-stat">
                    <span className="stat-label">Total Women Trained</span>
                    <span className="stat-value">{stats?.total_women || 0}</span>
                  </div>
                  <div className="system-stat">
                    <span className="stat-label">Active Women</span>
                    <span className="stat-value">{stats?.active_women || 0}</span>
                  </div>
                  <div className="system-stat">
                    <span className="stat-label">Departments</span>
                    <span className="stat-value">7</span>
                  </div>
                  <div className="system-stat">
                    <span className="stat-label">Success Rate</span>
                    <span className="stat-value">{stats?.success_rate || 0}%</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="analytics-section">
              <h3><FaChartLine /> Analytics Overview</h3>
              <div className="analytics-grid">
                <div className="analytics-card">
                  <h4>Monthly Revenue</h4>
                  <div className="chart-placeholder">
                    <FaChartLine className="chart-icon" />
                    <p>Revenue chart will appear here</p>
                  </div>
                </div>
                <div className="analytics-card">
                  <h4>User Growth</h4>
                  <div className="chart-placeholder">
                    <FaUsers className="chart-icon" />
                    <p>User growth chart will appear here</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* User Detail Modal */}
      {selectedUser && (
        <div className="modal-overlay">
          <div className="modal-content user-modal">
            <button className="close-modal" onClick={() => setSelectedUser(null)}>
              &times;
            </button>
            <div className="modal-header">
              <h2>User Details</h2>
            </div>
            <div className="modal-body">
              <div className="user-detail-header">
                <div className="user-avatar-large">
                  {selectedUser.username.charAt(0).toUpperCase()}
                </div>
                <div className="user-detail-info">
                  <h3>{selectedUser.full_name || selectedUser.username}</h3>
                  <p>{selectedUser.email}</p>
                  <div className="user-tags">
                    <span className={`user-type-badge type-${selectedUser.user_type}`}>
                      {selectedUser.user_type}
                    </span>
                    <span className={`status-badge status-${selectedUser.status || 'active'}`}>
                      {selectedUser.status || 'active'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="user-detail-grid">
                <div className="detail-item">
                  <strong>Username:</strong>
                  <span>{selectedUser.username}</span>
                </div>
                <div className="detail-item">
                  <strong>Email:</strong>
                  <span>{selectedUser.email}</span>
                </div>
                <div className="detail-item">
                  <strong>Phone:</strong>
                  <span>{selectedUser.phone || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <strong>Joined:</strong>
                  <span>{new Date(selectedUser.created_at).toLocaleDateString()}</span>
                </div>
                <div className="detail-item">
                  <strong>Last Login:</strong>
                  <span>{selectedUser.last_login ? new Date(selectedUser.last_login).toLocaleString() : 'Never'}</span>
                </div>
              </div>

              <div className="modal-actions">
                <button className="btn btn-outline" onClick={() => setSelectedUser(null)}>
                  Close
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={() => window.location.href = `/admin/users/${selectedUser.id}/edit`}
                >
                  <FaEdit /> Edit User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;